/* smööts scroll reveal: <sm-scroll-reveal> custom element.
   One passive scroll listener + requestAnimationFrame. Progress is tied to scroll
   position and eased with a light lerp so the scrub feels smooth, never jumpy.
   Per frame it writes only transform, opacity and text colour. The final layout
   (headline scale, bottle size) is measured once per resize, not per frame. */
(function () {
  if (customElements.get('sm-scroll-reveal')) return;

  var clamp = function (v) { return v < 0 ? 0 : v > 1 ? 1 : v; };
  var seg = function (p, a, b) { return clamp((p - a) / (b - a)); };
  var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };
  var easeInOut = function (t) { return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
  var backOut = function (t) { var c = 1.6; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };
  var hex = function (h) { h = (h || '').trim().replace('#', ''); if (h.length === 3) h = h.replace(/./g, '$&$&'); return [0, 2, 4].map(function (i) { return parseInt(h.substr(i, 2), 16) || 0; }); };
  var mix = function (a, b, t) { return 'rgb(' + a.map(function (v, i) { return Math.round(v + (b[i] - v) * t); }).join(',') + ')'; };

  class SmScrollReveal extends HTMLElement {
    connectedCallback() {
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.lines = Array.prototype.slice.call(this.querySelectorAll('.smsr__line'));
      this.inners = this.lines.map(function (l) { return l.firstElementChild; });
      this.headline = this.querySelector('.smsr__headline');
      this.bgEnd = this.querySelector('.smsr__bg--end');
      this.floats = Array.prototype.slice.call(this.querySelectorAll('.smsr__float'));
      this.product = this.querySelector('.smsr__product');
      this.bottle = this.querySelector('.smsr__bottle');
      this.peek = this.querySelector('.smsr__peek');
      this.readColors();
      this.target = 0; this.cur = -1; this.raf = 0;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.bottle && !this.bottle.complete) this.bottle.addEventListener('load', this.onResize, { once: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.onResize);
      this.onResize();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.raf);
    }
    /* Colours come from the CSS custom properties, so a theme setting (or the
       preview's palette switch) only has to change the variables and call this. */
    readColors() {
      var cs = getComputedStyle(this);
      this.cCream = hex(cs.getPropertyValue('--smsr-cream') || '#F4F0E4');
      this.cAccent = hex(cs.getPropertyValue('--smsr-accent') || '#E2EBA2');
      this.cAccentEnd = hex(cs.getPropertyValue('--smsr-accent-end') || '#15803D');
      this.cInk = hex(cs.getPropertyValue('--smsr-ink') || '#181419');
      if (this.cur >= -1) this.render(this.cur);
    }
    /* Measure the final composition: all headline lines at the top (scaled to fit),
       the bottle centred underneath, only slightly cropped by the bottom edge. */
    layout() {
      var vh = window.innerHeight, vw = window.innerWidth, mobile = vw < 750;
      this.headline.style.transform = 'none';
      if (mobile) return this.layoutMobile(vh, vw);
      var hh = this.headline.offsetHeight, top0 = this.headline.offsetTop;
      var topPad = Math.max(24, vh * (mobile ? .07 : .05));
      var s = Math.min(1, (vh * (mobile ? .3 : .29)) / hh);
      this.L = { vh: vh, hh: hh, s: s, dy: topPad - top0 };
      var reserved = topPad + hh * s + vh * (mobile ? .025 : .02);
      var ar = (this.bottle.naturalWidth || +this.bottle.getAttribute('width')) / (this.bottle.naturalHeight || +this.bottle.getAttribute('height')) || .45;
      var crop = .06;                                   /* share of the bottle hidden below the edge */
      var h = (vh - reserved) / (1 - crop);
      h = Math.min(h, (vw * (mobile ? .86 : .5)) / ar);
      this.product.style.height = Math.round(h) + 'px';
      this.product.style.width = Math.round(h * ar) + 'px';
      this.L.h = h; this.L.endY = Math.max(0, h - (vh - reserved));
    }
    /* Mobile (Grüns-style): the bottle is on screen from the first frame, whole and
       centred low. Lines reveal in place at the top, the background turns, and the
       capsule peeks out last. Nothing glides or rescales, so it reads on a small screen. */
    layoutMobile(vh, vw) {
      /* fit the widest line to the screen width (lines never wrap on mobile) */
      var wMax = 0;
      this.inners.forEach(function (el) { wMax = Math.max(wMax, el.scrollWidth); });
      var s = wMax ? Math.min(1.25, (vw - 24) / wMax) : 1;
      var hh = this.headline.offsetHeight, top0 = this.headline.offsetTop;
      var topPad = vh * .19, bottomGap = vh * .1;
      var ar = (this.bottle.naturalWidth || +this.bottle.getAttribute('width')) / (this.bottle.naturalHeight || +this.bottle.getAttribute('height')) || .45;
      var textBottom = topPad + hh * s;
      var h = Math.min(vh * .42, vh - textBottom - bottomGap - vh * .07);
      h = Math.max(150, Math.min(h, (vw * .5) / ar));
      this.product.style.height = Math.round(h) + 'px';
      this.product.style.width = Math.round(h * ar) + 'px';
      this.L = { vh: vh, hh: hh, s: s, dy: topPad - top0, h: h, endY: -bottomGap, mobile: true };
    }
    onResize() {
      if (this.reduced.matches) return;
      this.layout();
      this.target = this.cur = this.progress();
      this.render(this.cur);
    }
    /* Progress runs from -1 (section top at the viewport bottom) through 0 (pinned)
       to 1 (end of the pinned run), so line 1 reveals while the section scrolls in. */
    progress() {
      var r = this.getBoundingClientRect(), vh = window.innerHeight;
      if (r.top > 0) return -clamp(r.top / vh);
      var run = r.height - vh;
      return run > 0 ? clamp(-r.top / run) : 0;
    }
    onScroll() {
      if (this.reduced.matches) return;
      this.target = this.progress();
      if (window.__smsrNoLerp) { this.cur = this.target; this.render(this.cur); return; }
      if (!this.raf) this.raf = requestAnimationFrame(this.tick);
    }
    tick() {
      this.raf = 0;
      var d = this.target - this.cur;
      this.cur = Math.abs(d) < .0004 ? this.target : this.cur + d * .14;
      this.render(this.cur);
      if (this.cur !== this.target) this.raf = requestAnimationFrame(this.tick);
    }
    /* Timeline, 6 steps (no idle step; the section opens on line 1):
       1  entry -0.45..0   line 1 slides up while the section scrolls in and pins
       2  0.00-0.14        line 2
       3  0.14-0.28        line 3
       4  0.28-0.44        accent line 4, background wipes up into the light tone, text turns ink
       5  0.46-0.76        headline glides to the top, bottle rises in underneath
       6  0.72-0.90        capsule peeks out of the neck and settles; hold, then the pin releases */
    render(p) {
      if (!this.L) return;
      if (this.L.mobile) return this.renderMobile(p);
      var n = this.inners.length, i, t, L = this.L;
      for (i = 0; i < n; i++) {
        var a = i === 0 ? -.45 : (i - 1) * .14, b = i === 0 ? 0 : i * .14;
        if (i === n - 1 && n > 1) { a = (n - 2) * .14; b = a + .16; }
        t = easeOut(seg(p, a, b));
        this.inners[i].style.transform = 'translate3d(0,' + ((1 - t) * 112).toFixed(2) + '%,0)';
      }
      var bgT = easeInOut(seg(p, .28, .46));
      this.bgEnd.style.transform = 'translate3d(0,' + ((1 - bgT) * 100 - bgT * 26).toFixed(2) + '%,0)';
      var inkT = seg(p, .33, .43);
      this.style.color = mix(this.cCream, this.cInk, inkT);
      for (i = 0; i < n; i++) {
        if (this.lines[i].classList.contains('smsr__line--accent')) this.lines[i].style.color = mix(this.cAccent, this.cAccentEnd, inkT);
      }
      var mT = easeInOut(seg(p, .46, .76));
      var sc = 1 + (L.s - 1) * mT;
      this.headline.style.transform = 'translate3d(0,' + (L.dy * mT).toFixed(1) + 'px,0) scale(' + sc.toFixed(4) + ')';

      /* floats keep bobbing at the edges; they drift outward a little as the bottle arrives */
      var fT = easeInOut(seg(p, .46, .76));
      this.floats.forEach(function (f, k) {
        var dir = parseFloat(f.style.getPropertyValue('--x')) < 50 ? -1 : 1;
        var depth = .6 + (k % 3) * .25;
        var y = -Math.max(p, 0) * 40 * depth;
        f.style.transform = 'translate3d(' + (dir * fT * 4).toFixed(2) + 'vw,' + y.toFixed(1) + 'px,0) rotate(' + (dir * fT * 10).toFixed(1) + 'deg)';
      });

      var bT = easeOut(seg(p, .5, .8));
      var y = (1 - bT) * (L.h + 40) + bT * L.endY;
      this.product.style.transform = 'translate3d(-50%,' + y.toFixed(1) + 'px,0)';
      if (this.peek) {
        var cT = backOut(seg(p, .72, .9));
        this.peek.style.transform = 'translate3d(0,' + ((1 - cT) * 100).toFixed(2) + '%,0)';
      }
    }
    /* Mobile timeline, 6 steps:
       0  pinned            bottle + floating capsules, no text yet
       1-4  0.03-0.52       lines 1 to 4 rise in at the top, one per scroll
       5  0.38-0.58         background turns into the light tone, text to ink
       6  0.56-0.74         capsule peeks out of the neck; hold, then the pin releases */
    renderMobile(p) {
      var n = this.inners.length, i, t, L = this.L, step = .49 / Math.max(1, n);
      for (i = 0; i < n; i++) {
        var a = .03 + i * step;
        t = easeOut(seg(p, a, a + step));
        this.inners[i].style.transform = 'translate3d(0,' + ((1 - t) * 112).toFixed(2) + '%,0)';
      }
      var bgT = easeInOut(seg(p, .38, .58));
      this.bgEnd.style.transform = 'translate3d(0,' + ((1 - bgT) * 100 - bgT * 26).toFixed(2) + '%,0)';
      var inkT = seg(p, .42, .54);
      this.style.color = mix(this.cCream, this.cInk, inkT);
      for (i = 0; i < n; i++) {
        if (this.lines[i].classList.contains('smsr__line--accent')) this.lines[i].style.color = mix(this.cAccent, this.cAccentEnd, inkT);
      }
      this.headline.style.transform = 'translate3d(0,' + L.dy.toFixed(1) + 'px,0) scale(' + L.s.toFixed(4) + ')';
      this.floats.forEach(function (f, k) {
        var dir = parseFloat(f.style.getPropertyValue('--xm') || f.style.getPropertyValue('--x')) < 50 ? -1 : 1;
        var depth = .6 + (k % 3) * .25;
        var y = -Math.max(p, 0) * 60 * depth;
        f.style.transform = 'translate3d(' + (dir * Math.max(p, 0) * 3).toFixed(2) + 'vw,' + y.toFixed(1) + 'px,0)';
      });
      this.product.style.transform = 'translate3d(-50%,' + L.endY.toFixed(1) + 'px,0)';
      if (this.peek) {
        var cT = backOut(seg(p, .56, .74));
        this.peek.style.transform = 'translate3d(0,' + ((1 - cT) * 100).toFixed(2) + '%,0)';
      }
    }
  }
  customElements.define('sm-scroll-reveal', SmScrollReveal);
})();
